import { useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { useAuth } from '@/contexts/AuthContext';
import {
  useAdminCollection,
  createAdminDoc,
  updateAdminDoc,
  deleteAdminDoc,
} from '@/hooks/useAdminCollection';
import { SprintTask, SprintStatus, SprintPriority } from '@/types';

const COLLECTION = 'sprintTasks';

const STATUSES: SprintStatus[] = ['BACKLOG', 'TODO', 'IN_PROGRESS', 'DONE'];
const STATUS_LABELS: Record<SprintStatus, string> = {
  BACKLOG: 'Backlog',
  TODO: 'To Do',
  IN_PROGRESS: 'In Progress',
  DONE: 'Done',
};

const PRIORITIES: SprintPriority[] = ['LOW', 'MEDIUM', 'HIGH'];
const PRIORITY_COLORS: Record<SprintPriority, string> = {
  LOW: 'var(--text-3)',
  MEDIUM: 'var(--accent)',
  HIGH: 'var(--danger)',
};

interface TaskForm {
  title: string;
  description: string;
  status: SprintStatus;
  priority: SprintPriority;
  assignee: string;
}

const EMPTY_FORM: TaskForm = { title: '', description: '', status: 'BACKLOG', priority: 'MEDIUM', assignee: '' };

function SprintContent() {
  const { currentUser } = useAuth();
  const { items: tasks, loading } = useAdminCollection<SprintTask>(COLLECTION, 'updatedAt');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<SprintTask | null>(null);
  const [form, setForm] = useState<TaskForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<SprintStatus | null>(null);

  const byStatus = useMemo(() => {
    const map = Object.fromEntries(STATUSES.map((s) => [s, [] as SprintTask[]])) as Record<SprintStatus, SprintTask[]>;
    for (const t of tasks) (map[t.status] ||= []).push(t);
    return map;
  }, [tasks]);

  const openAdd = (status?: SprintStatus) => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, status: status || 'BACKLOG' });
    setError('');
    setShowForm(true);
  };

  const openEdit = (task: SprintTask) => {
    setEditing(task);
    setForm({
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority,
      assignee: task.assignee || '',
    });
    setError('');
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return setError('Title is required');
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        status: form.status,
        priority: form.priority,
        assignee: form.assignee.trim() || undefined,
      };
      if (editing) {
        await updateAdminDoc(COLLECTION, editing.id, payload);
      } else {
        await createAdminDoc(COLLECTION, payload, currentUser!.uid);
      }
      setShowForm(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const moveTask = async (task: SprintTask, status: SprintStatus) => {
    if (task.status === status) return;
    try {
      await updateAdminDoc(COLLECTION, task.id, { status });
    } catch (err) {
      console.error('Failed to move task:', err);
    }
  };

  const handleDrop = (status: SprintStatus) => {
    setDragOverCol(null);
    const task = tasks.find((t) => t.id === draggingId);
    setDraggingId(null);
    if (task) moveTask(task, status);
  };

  const handleDelete = async (task: SprintTask) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    try {
      await deleteAdminDoc(COLLECTION, task.id);
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <div className="terminal-card">
        <div className="card-header">
          <span className="card-title">Sprint Board</span>
          <div className="card-header-right">
            <span className="stat-chip">Tasks <strong>{tasks.length}</strong></span>
            <button className="action-btn large" onClick={() => openAdd()}>+ Add Task</button>
          </div>
        </div>
        <div className="card-body" style={{ padding: '16px' }}>
          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: 'var(--muted)', fontSize: '11px', letterSpacing: '0.12em' }}>
              Loading…
            </div>
          ) : (
            <div className="sprint-board">
              {STATUSES.map((status) => (
                <div
                  key={status}
                  className={`sprint-col${dragOverCol === status ? ' sprint-col--dragover' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); if (dragOverCol !== status) setDragOverCol(status); }}
                  onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOverCol(null); }}
                  onDrop={() => handleDrop(status)}
                >
                  <div className="sprint-col__head">
                    <span>{STATUS_LABELS[status]}</span>
                    <span className="sprint-col__count">{byStatus[status].length}</span>
                  </div>
                  <div className="sprint-col__body">
                    {byStatus[status].map((task) => {
                      const idx = STATUSES.indexOf(status);
                      return (
                        <div
                          key={task.id}
                          className={`sprint-task${draggingId === task.id ? ' sprint-task--dragging' : ''}`}
                          draggable
                          onDragStart={(e) => { setDraggingId(task.id); e.dataTransfer.effectAllowed = 'move'; }}
                          onDragEnd={() => { setDraggingId(null); setDragOverCol(null); }}
                        >
                          <div className="sprint-task__top">
                            <span className="sprint-task__title">{task.title}</span>
                            <span className="sprint-pri" style={{ color: PRIORITY_COLORS[task.priority] }}>{task.priority}</span>
                          </div>
                          {task.description && <p className="sprint-task__desc">{task.description}</p>}
                          {task.assignee && <div className="sprint-task__assignee">@{task.assignee}</div>}
                          <div className="sprint-task__actions">
                            <button
                              className="sprint-move"
                              disabled={idx === 0}
                              onClick={() => moveTask(task, STATUSES[idx - 1])}
                              title="Move left"
                            >‹</button>
                            <button
                              className="sprint-move"
                              disabled={idx === STATUSES.length - 1}
                              onClick={() => moveTask(task, STATUSES[idx + 1])}
                              title="Move right"
                            >›</button>
                            <button className="action-btn secondary crm-mini-btn" onClick={() => openEdit(task)}>Edit</button>
                            <button className="action-btn danger crm-mini-btn" onClick={() => handleDelete(task)}>Del</button>
                          </div>
                        </div>
                      );
                    })}
                    <button className="sprint-add" onClick={() => openAdd(status)}>+ Add</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}>
          <div className="terminal-card" style={{ maxWidth: '480px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="card-header">
              <span className="card-title">{editing ? 'Edit Task' : 'Add Task'}</span>
            </div>
            <form className="card-body" onSubmit={handleSave}>
              {error && <div className="terminal-alert error">Error: {error}</div>}
              <div className="field-group">
                <label className="field-label">Title *</label>
                <input className="terminal-input" value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Task title" autoFocus />
              </div>
              <div className="field-group">
                <label className="field-label">Description</label>
                <textarea className="terminal-textarea" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Details…" />
              </div>
              <div className="crm-form-row">
                <div className="field-group">
                  <label className="field-label">Status</label>
                  <select className="terminal-select" value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value as SprintStatus })}>
                    {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                  </select>
                </div>
                <div className="field-group">
                  <label className="field-label">Priority</label>
                  <select className="terminal-select" value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value as SprintPriority })}>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="field-group">
                <label className="field-label">Assignee</label>
                <input className="terminal-input" value={form.assignee}
                  onChange={(e) => setForm({ ...form, assignee: e.target.value })} placeholder="Name or handle" />
              </div>
              <div className="btn-row">
                <button type="submit" className="action-btn large" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Task'}
                </button>
                <button type="button" className="action-btn secondary" onClick={() => setShowForm(false)} disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminSprintPage() {
  return (
    <ProtectedRoute requireAdmin>
      <Layout wide>
        <SprintContent />
      </Layout>
    </ProtectedRoute>
  );
}
